import React, { useEffect, useState } from 'react';
import { Button, Modal, Space, Spin } from 'antd';
import { Link } from 'react-router-dom';
import { PrinterOutlined } from '@ant-design/icons';
import { AuditSchedule } from 'modules/auditSchedule/types';
import { Colors } from 'Layout/Colors';
import { useTranslation } from 'react-i18next';
import { isAfter, isBefore, startOfYear, endOfYear } from 'date-fns';
import firebase from '../../firebase';
import './yearlyOverview.css';

interface YearlyTaskOverviewProps {
  visible: boolean;
  onClose: () => void;
  orgId: string;
  year?: number;
}

type TaskStatus =
  | 'error'
  | 'processing'
  | 'success'
  | 'inprogress'
  | 'warning'
  | 'hardWarning'
  | 'voided'
  | null;

interface TaskStatusWithId {
  status: TaskStatus;
  id: string;
}

interface YearlyDataRow {
  subject: string;
  months: { [key: number]: TaskStatusWithId | TaskStatusWithId[] }; // 0 = Jan, 11 = Dec
}

/**
 * Transforms AuditSchedule data into YearlyDataRow format
 * @param schedules - Array of AuditSchedule objects
 * @param year - The year to filter by (defaults to current year)
 * @returns Array of YearlyDataRow objects grouped by templateTitle
 */
const transformAuditSchedulesToYearlyData = (
  schedules: AuditSchedule[],
  year?: number
): YearlyDataRow[] => {
  const targetYear = year ?? new Date().getFullYear();

  // Filter schedules for the target year
  const yearSchedules = schedules.filter((schedule) => {
    const dueDate = schedule.dueDate.toDate();
    return dueDate.getFullYear() === targetYear;
  });

  // Group by templateTitle
  const grouped = yearSchedules.reduce((acc, schedule) => {
    const title = schedule.templateTitle;
    if (!acc[title]) {
      acc[title] = [];
    }
    acc[title].push(schedule);
    return acc;
  }, {} as { [title: string]: AuditSchedule[] });

  // Convert to YearlyDataRow format
  return Object.entries(grouped).map(([title, schedules]) => {
    const months: { [key: number]: TaskStatusWithId | TaskStatusWithId[] } = {};

    schedules.forEach((schedule) => {
      const month = schedule.dueDate.toDate().getMonth(); // 0-11
      const now = new Date();
      const dueDate = schedule.dueDate.toDate();

      // Determine status - matching AuditScheduleList logic
      let status: TaskStatus = 'processing';

      if (schedule.isCompleted && schedule.hasOnHoldTasks) {
        status = 'warning';
      } else if (schedule.isCompleted && schedule.needsResolution) {
        status = 'hardWarning';
      } else if (schedule.isCompleted) {
        status = 'success';
      } else if (dueDate < now) {
        status = 'error';
      } else if (isAfter(now, schedule.startDate.toDate()) && isBefore(now, dueDate)) {
        status = 'inprogress';
      } else {
        status = 'processing';
      }

      const taskWithId: TaskStatusWithId = { status, id: schedule.id };

      // Handle multiple schedules in same month
      if (months[month]) {
        if (Array.isArray(months[month])) {
          (months[month] as TaskStatusWithId[]).push(taskWithId);
        } else {
          months[month] = [months[month] as TaskStatusWithId, taskWithId];
        }
      } else {
        months[month] = taskWithId;
      }
    });

    return {
      subject: title,
      months,
    };
  });
};

const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAJ', 'JUN', 'JUL', 'AUG', 'SEP', 'OKT', 'NOV', 'DEC'];

const YearlyTaskOverview: React.FC<YearlyTaskOverviewProps> = ({
  visible,
  onClose,
  orgId,
  year,
}) => {
  const { t } = useTranslation();
  const [auditSchedules, setAuditSchedules] = useState<AuditSchedule[]>([]);
  const [loading, setLoading] = useState(true);

  const targetYear = year ?? new Date().getFullYear();
  const yearlyData = transformAuditSchedulesToYearlyData(auditSchedules, targetYear);

  useEffect(() => {
    if (!visible || !orgId) return;

    setLoading(true);
    const yearStart = startOfYear(new Date(targetYear, 0, 1));
    const yearEnd = endOfYear(new Date(targetYear, 0, 1));

    const unsubscribe = firebase
      .firestore()
      .collection('orgs')
      .doc(orgId)
      .collection('auditSchedules')
      .where('dueDate', '>=', yearStart)
      .where('dueDate', '<=', yearEnd)
      .orderBy('dueDate', 'asc')
      .onSnapshot(
        (query) => {
          const list = query.docs.map(
            (doc) =>
              ({
                id: doc.id,
                ...doc.data(),
              } as AuditSchedule)
          );
          setAuditSchedules(list);
          setLoading(false);
        },
        (error) => {
          console.error('Error fetching audit schedules:', error);
          setLoading(false);
        }
      );

    return () => unsubscribe();
  }, [visible, orgId, targetYear]);

  const getStatusLabel = (status: TaskStatus) => {
    switch (status) {
      case 'success':
        return 'X';
      case 'processing':
        return 'X';
      case 'error':
        return '!';
      case 'hardWarning':
        return '!';
      case 'warning':
        return '!';
      case 'inprogress':
        return 'X';
      case 'voided':
        return '-';
      default:
        return 'X';
    }
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const styles = `
      <style>
        @page {
          size: landscape;
          margin: 10mm;
        }
        body {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
          padding: 20px;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        .yearly-overview-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 12px;
        }
        .yearly-overview-table th,
        .yearly-overview-table td {
          border: 1px solid #d9d9d9;
          padding: 4px 8px;
          text-align: center;
          vertical-align: middle;
        }
        .yearly-overview-table th {
          background-color: #f0f0f0;
          font-weight: bold;
        }
        .yearly-overview-table th.subject-col {
          text-align: left;
          width: 200px;
        }
        
        .cell-content {
          display: flex;
          flex-wrap: wrap;
          gap: 2px;
          justify-content: center;
          align-items: center;
        }
        
        .status-badge {
          font-weight: bold;
          color: #333;
          padding: 2px 4px;
          border-radius: 2px;
          min-width: 16px;
          display: inline-block;
        }
        
        .status-error { background-color: ${Colors.danger}; color: white; }
        .status-success { background-color: ${Colors.done}; color: white; }
        .status-processing { background-color: ${Colors.processing}; color: white; }
        .status-hardWarning { background-color: ${Colors.hardWarning}; color: white; }
        .status-warning { background-color: ${Colors.warning}; color: #614700; }
        .status-inprogress { background-color: ${Colors.inprogress}; color: #006d75; }
        .status-voided { background-color: ${Colors.voided}; color: white; }
        
        .status-legend {
          margin-bottom: 20px;
          display: flex;
          gap: 16px;
          flex-wrap: wrap;
        }
        .legend-item {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 12px;
        }
        .legend-color {
          width: 20px;
          height: 20px;
          border: 1px solid #d9d9d9;
        }
        h1 { margin-bottom: 20px; }
      </style>
    `;

    const legendHtml = `
      <div class="status-legend">
        <div class="legend-item"><div class="legend-color status-error"></div><span>${t(
          'audit.schedule.legend.error'
        )}</span></div>
        <div class="legend-item"><div class="legend-color status-inprogress"></div><span>${t(
          'audit.schedule.legend.inprogress'
        )}</span></div>
        <div class="legend-item"><div class="legend-color status-warning"></div><span>${t(
          'audit.schedule.legend.warning'
        )}</span></div>
        <div class="legend-item"><div class="legend-color status-hardWarning"></div><span>${t(
          'audit.schedule.legend.hardWarning'
        )}</span></div>
        <div class="legend-item"><div class="legend-color status-processing"></div><span>${t(
          'audit.schedule.legend.processing'
        )}</span></div>
        <div class="legend-item"><div class="legend-color status-success"></div><span>${t(
          'audit.schedule.legend.success'
        )}</span></div>
      </div>
    `;

    const tableRows = yearlyData
      .map((row) => {
        const monthCells = months
          .map((_, index) => {
            const statusOrArray = row.months[index];
            let cellContent = '';

            if (Array.isArray(statusOrArray)) {
              cellContent = statusOrArray
                .map((item) => {
                  const className = item.status
                    ? `status-badge status-${item.status}`
                    : 'status-badge';
                  const label = getStatusLabel(item.status);
                  return `<span class="${className}">${label}</span>`;
                })
                .join('');
            } else {
              const item = statusOrArray;
              const className = item?.status
                ? `status-badge status-${item.status}`
                : 'status-badge';
              const label = getStatusLabel(item?.status);
              // Only render if there is a status, otherwise empty cell
              if (item) {
                cellContent = `<span class="${className}">${label}</span>`;
              }
            }

            return `<td><div class="cell-content">${cellContent}</div></td>`;
          })
          .join('');

        return `
        <tr>
          <td style="text-align: left;">${row.subject}</td>
          ${monthCells}
        </tr>
      `;
      })
      .join('');

    const tableHtml = `
      <table class="yearly-overview-table">
        <thead>
          <tr>
            <th class="subject-col">Emne</th>
            ${months.map((m) => `<th>${m}</th>`).join('')}
          </tr>
        </thead>
        <tbody>
          ${tableRows}
        </tbody>
      </table>
    `;

    const htmlContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Årshjul Oversigt</title>
          ${styles}
        </head>
        <body>
          <h1>Årshjul Oversigt</h1>
          ${legendHtml}
          ${tableHtml}
          <script>
            window.onload = function() {
              window.print();
              window.onafterprint = function() {
                window.close();
              }
            }
          </script>
        </body>
      </html>
    `;

    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  return (
    <Modal
      title={
        <Space>
          <span>Årshjul Oversigt</span>
          <Button icon={<PrinterOutlined />} onClick={handlePrint}>
            Print
          </Button>
        </Space>
      }
      visible={visible}
      onCancel={onClose}
      width="100%"
      style={{ top: 0, height: '100vh', maxWidth: '100vw' }}
      bodyStyle={{ height: 'calc(100vh - 55px)', overflow: 'auto' }}
      footer={null}
      wrapClassName="yearly-overview-modal"
    >
      <div className="yearly-overview-container">
        <div className="status-legend">
          <div className="legend-item">
            <div className="legend-color status-error"></div>
            <span>{t('audit.schedule.legend.error')}</span>
          </div>
          <div className="legend-item">
            <div className="legend-color status-inprogress"></div>
            <span>{t('audit.schedule.legend.inprogress')}</span>
          </div>
          <div className="legend-item">
            <div className="legend-color status-warning"></div>
            <span>{t('audit.schedule.legend.warning')}</span>
          </div>
          <div className="legend-item">
            <div className="legend-color status-hardWarning"></div>
            <span>{t('audit.schedule.legend.hardWarning')}</span>
          </div>
          <div className="legend-item">
            <div className="legend-color status-processing"></div>
            <span>{t('audit.schedule.legend.processing')}</span>
          </div>
          <div className="legend-item">
            <div className="legend-color status-success"></div>
            <span>{t('audit.schedule.legend.success')}</span>
          </div>
        </div>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
            <Spin size="large" />
          </div>
        ) : yearlyData.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
            {t('audit.schedule.noSchedules', 'Ingen planlagte opgaver for dette år')}
          </div>
        ) : (
          <table className="yearly-overview-table">
            <thead>
              <tr>
                <th className="subject-col">Emne</th>
                {months.map((month) => (
                  <th key={month}>{month}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {yearlyData.map((row, index) => (
                <React.Fragment key={index}>
                  <tr>
                    <td style={{ textAlign: 'left' }}>{row.subject}</td>
                    {months.map((_, monthIndex) => {
                      const statusOrArray = row.months[monthIndex];
                      return (
                        <td key={monthIndex}>
                          <div
                            style={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: 2,
                              justifyContent: 'center',
                            }}
                          >
                            {Array.isArray(statusOrArray) ? (
                              statusOrArray.map((item, i) => (
                                <Link
                                  key={i}
                                  to={`/orgs/${orgId}/audit/audits/${item.id}`}
                                  onClick={onClose}
                                >
                                  <span
                                    className={`status-badge ${
                                      item.status ? `status-${item.status}` : ''
                                    }`}
                                  >
                                    {getStatusLabel(item.status)}
                                  </span>
                                </Link>
                              ))
                            ) : statusOrArray ? (
                              <Link
                                to={`/orgs/${orgId}/audit/audits/${statusOrArray.id}`}
                                onClick={onClose}
                              >
                                <span
                                  className={`status-badge ${
                                    statusOrArray.status ? `status-${statusOrArray.status}` : ''
                                  }`}
                                >
                                  {getStatusLabel(statusOrArray.status)}
                                </span>
                              </Link>
                            ) : null}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </Modal>
  );
};

export default YearlyTaskOverview;
